import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardHeader,
	CardWrapper,
	CtaButton,
	DeleteConfirmModal,
	EmptyTable,
	Header,
	NameDialog,
	ProductSettingsTabs,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useSearch, useTableSelect } from 'shared/hooks'
import type { FabricItem } from 'features/fabrics/types/fabrics.types'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useFetchFabrics } from './queries/useFetchFabrics'
import { useCreateFabricMutation } from './mutations/useCreateFabricMutation'
import { useEditFabricMutation } from './mutations/useEditFabricMutation'
import { useDeleteFabricsMutation } from './mutations/useDeleteFabricsMutation'
import { useDeleteFabricMutation } from './mutations/useDeleteFabricMutation'
import { TableSection } from './organisms/TableSection'

const FabricsPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleSearch } = useSearch({ name: 'search' })
	const [checkedFabrics, setCheckedFabrics] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<string | null>(null)
	const [editedFabric, setEditedFabric] = useState<FabricItem | null>(null)
	const { fabrics, isFabricsLoading } = useFetchFabrics()
	const { select } = useTableSelect({ nodes: fabrics?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const [actionsFilter] = useActionFilters(!checkedFabrics.length)

	const {
		isOpen: isAddDialogOpen,
		openModal: openAddDialog,
		closeModal: closeAddDialog
	} = useModal()
	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()

	const { createFabric, createFabricLoading } = useCreateFabricMutation({
		onSuccess: closeAddDialog
	})

	const { editFabricMutation, isEditFabricLoading } = useEditFabricMutation({
		onSuccess: () => setEditedFabric(null)
	})

	const { deleteFabricMutation } = useDeleteFabricMutation({
		id: idToBeRemoved,
		onSettled: closeDeleteModal
	})

	const { deleteFabricsMutation } = useDeleteFabricsMutation({
		ids: checkedFabrics,
		onSettled: () => {
			select.fns.onRemoveAll()
			closeBulkDeleteModal()
		}
	})

	function onSelectChange(action: any, state: any) {
		setCheckedFabrics(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id.toString())
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onEdit: setEditedFabric, onDelete: handleDelete })

	const handleAction = (action: string) => {
		if (action === 'delete') openBulkDeleteModal()
	}

	const renderContent = () => {
		if (isFabricsLoading) return <EmptyTable isLoading />
		if (!fabrics?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No fabrics yet')} />

		return <TableSection fabrics={fabrics} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Products')}
				tabs={<ProductSettingsTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Fabrics')}
						description={t('Fabric types that can be selected on a product.')}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search fabrics')}
							placeholder={t('Search fabrics')}
							onChange={handleSearch}
						/>
						<CtaButton text={t('Add fabric')} onClick={openAddDialog} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isAddDialogOpen && (
				<NameDialog
					title={t('Add fabric')}
					submitText={t('Create')}
					isLoading={createFabricLoading}
					closeModal={closeAddDialog}
					onSubmit={createFabric}
				/>
			)}
			{editedFabric && (
				<NameDialog
					title={t('Edit fabric')}
					submitText={t('Save')}
					defaultValue={editedFabric.name}
					isLoading={isEditFabricLoading}
					closeModal={() => setEditedFabric(null)}
					onSubmit={(data) => editFabricMutation({ id: editedFabric.id, data })}
				/>
			)}
			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete fabric')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={deleteFabricMutation}>
					<p>{t('You are about to remove a fabric. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected fabrics')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={deleteFabricsMutation}>
					<p>
						{t('You are about to remove {{ count }} fabrics. Are you sure you want to proceed?', {
							count: checkedFabrics.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
		</>
	)
}

export default FabricsPage
